import {Injectable} from '@angular/core';
import {MappingEnginePostReq} from '@em/shared/api-interface';
import {
  BackendCondition,
  BackendConditionLink,
  BackendConditionLinks,
  BackendConditions,
  BackendMappingRule,
  BackendMappingValue,
  CalculationMappingValue,
  ConditionalStatementList,
  MappingEngine,
  MappingRule,
  MappingRuleList,
  MappingRuleType,
  MappingValue,
  SimpleMappingValue,
} from '@em/shared/mapping-engine/domain-models';
import {v4 as uuidV4} from 'uuid';

@Injectable({
  providedIn: 'root',
})
export class BackEndModelBuilderService {
  constructor() {}

  buildMappingEngine(mappingEngine: MappingEngine): MappingEnginePostReq {
    return {
      is_default: !!mappingEngine.isDefault,
      attributes: this.buildAttributes(mappingEngine.mappingRuleList), // Keep attributes at last, back-end requirement
    };
  }

  buildAttributes(
    mappingRuleList: MappingRuleList[],
  ): MappingEnginePostReq['attributes'] {
    return mappingRuleList.map((mappingRule, mappingRuleIndex) => ({
      attribute: mappingRule.attribute,
      strip_html: !!mappingRule.stripHTML,
      rules: mappingRule.mappingRules.map((rule, index) =>
        this.buildRule(rule, index + 1),
      ),
      order: mappingRuleIndex,
      custom: mappingRule.custom,
    }));
  }

  buildRule(mappingRule: MappingRule, order: number): BackendMappingRule {
    const {conditions, links} = this.buildConditionsAndLinks(
      mappingRule.conditionalStatementList,
    );

    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map_to: mappingRule.mappingType as any,
      map_to_value: this.buildMapToValue(
        mappingRule.mappingValue,
        mappingRule.mappingType,
      ),
      additional_processor: mappingRule.additionalProcessor,
      order,
      conditions,
      links,
    };
  }

  buildMapToValue(
    mappingValue: MappingValue | undefined,
    mappingType: MappingRuleType | undefined,
  ): BackendMappingValue {
    if (!mappingValue || !mappingType) {
      return null;
    } else if (
      mappingType === 'combine_attributes' &&
      Array.isArray(mappingValue)
    ) {
      return mappingValue.map((op) => ({
        type: op.type,
        value: op.value || '',
      }));
    } else if (mappingType === 'calculation') {
      const mp = mappingValue as CalculationMappingValue;
      return {
        attribute_name: mp.attribute_name,
        operator: mp.operator,
        value: mp.value,
      };
    } else {
      const mp = mappingValue as SimpleMappingValue;
      return {
        type: mp.type,
        value: mp.value || '',
      };
    }
  }

  buildConditionsAndLinks(
    conditionalStatementList: ConditionalStatementList | undefined,
  ): {conditions: BackendConditions; links: BackendConditionLinks} {
    if (!conditionalStatementList) {
      return {conditions: [], links: []};
    }

    const allConditions: BackendConditions = [];
    const allLinks: BackendConditionLinks = [];

    allLinks.push({
      operator: conditionalStatementList?.logicalOperator || 'and',
      parent_uuid: null,
    });

    conditionalStatementList.conditionalStatements.forEach((statement) => {
      let itemLink: BackendConditionLink | undefined;

      const uuid = uuidV4();

      if (statement.logicalOperator && statement.conditions?.length > 1) {
        itemLink = {
          operator: statement.logicalOperator,
          parent_uuid: uuid,
        };
        allLinks.push(itemLink);
      }

      statement.conditions.forEach((condition) => {
        if (condition.leftOperand.value && condition.operator) {
          const backendCondition: BackendCondition = {
            parent_uuid: uuid,
            attribute: condition.leftOperand.value.toString(),
            compare_operator: condition.operator,
            compare_value: condition.rightOperand?.value
              ? {
                  type: condition.rightOperand?.type,
                  value: condition.rightOperand?.value.toString(),
                }
              : null,
          };

          allConditions.push(backendCondition);
        }
      });
    });

    return {
      conditions: allConditions,
      links: allLinks,
    };
  }
}
